/**
 * SOC 2 Compliant Password Policy
 *
 * Enforces enterprise-grade password requirements:
 * - Minimum 12 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special character
 * - Cannot contain the user's email or username
 *
 * SOC 2 CC6.1 (Logical Access Security), CC6.2 (Authentication)
 */

import { z } from 'zod';

/** Individual password requirement for UI display */
export interface PasswordRequirement {
  id: string;
  label: string;
  met: boolean;
}

/** Password strength levels */
export type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong';  // pragma: allowlist secret

/**
 * Check all password requirements and return their status.
 * Used by UI to show real-time validation feedback.
 */
export function getPasswordRequirements(password: string, email?: string): PasswordRequirement[] {
  const emailLocal = email?.split('@')[0]?.toLowerCase() || '';

  return [
    {
      id: 'length',
      label: 'At least 12 characters',
      met: password.length >= 12,
    },
    {
      id: 'uppercase',
      label: 'At least 1 uppercase letter',
      met: /[A-Z]/.test(password),
    },
    {
      id: 'lowercase',
      label: 'At least 1 lowercase letter',
      met: /[a-z]/.test(password),
    },
    {
      id: 'digit',
      label: 'At least 1 number',
      met: /\d/.test(password),
    },
    {
      id: 'special',
      label: 'At least 1 special character (!@#$%^&*...)',
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
    },
    {
      id: 'no-email',
      label: 'Cannot contain your email or username',
      met: !emailLocal || !password.toLowerCase().includes(emailLocal),
    },
  ];
}

/**
 * Calculate password strength score.
 */
export function getPasswordStrength(password: string, email?: string): PasswordStrength {
  const requirements = getPasswordRequirements(password, email);
  const metCount = requirements.filter(r => r.met).length;

  if (metCount <= 2) return 'weak';
  if (metCount <= 4) return 'fair';
  if (metCount <= 5) return 'good';
  return 'strong';
}

/** Color mapping for strength indicator */
export const PASSWORD_STRENGTH_COLORS: Record<PasswordStrength, string> = {
  weak: '#ff4d4f',
  fair: '#faad14',
  good: '#1890ff',
  strong: '#52c41a',
};

/**
 * Zod schema for password validation.
 * Use with React Hook Form + hookform/resolvers for form validation.
 */
export function createPasswordSchema(email?: string) {
  return z.string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[A-Z]/, 'Password must contain at least 1 uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least 1 lowercase letter')
    .regex(/\d/, 'Password must contain at least 1 number')
    .regex(
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/,
      'Password must contain at least 1 special character'
    )
    .refine(
      (val) => {
        if (!email) return true;
        const emailLocal = email.split('@')[0]?.toLowerCase() || '';
        return !emailLocal || !val.toLowerCase().includes(emailLocal);
      },
      { message: 'Password cannot contain your email or username' }
    );
}

/**
 * Validate a password against the policy.
 * Returns true if all requirements are met.
 */
export function isPasswordValid(password: string, email?: string): boolean {
  return getPasswordRequirements(password, email).every(r => r.met);
}
